import {
  RuleContext,
  ValueDateOffsetNode,
  ValueDifferenceNode,
  ValueNode,
  ValueProductNode,
  ValueRoundNode,
  ValueSumNode,
} from '@navstation-portal/reporting/data-model/ui';
import { evaluate, readCarryOverField, readReportField, readSection, readVesselField } from './evaluate';

/**
 * Resolves a value node to the number or the date the form applies.
 *
 * It returns undefined when the template gives no value, so the caller falls
 * back to the bound in its own markup. That covers three shapes: no node at
 * all, a node the resolver cannot read, and a `when` node that takes a branch
 * with nothing in it. All three mean the template has nothing to say, so the
 * markup speaks.
 */
export function resolveValue(node: ValueNode | undefined, context: RuleContext): number | Date | undefined {
  if (node === undefined) {
    return undefined;
  }
  switch (node.type) {
    case 'const':
      return readValue(node.value);
    case 'reportField':
      // A field the user has not filled, and a path that reaches nothing, both
      // give no value, which leaves the markup bound in place.
      return readValue(readReportField(context, node.path));
    case 'carryOverField':
      // The report before this one. A missing carry-over report and a path
      // that reaches nothing both give no value, in the same way a field of
      // the report being edited does.
      return readValue(readCarryOverField(context, node.path));
    // A calculation can give a number that is not finite, or an Invalid Date,
    // for example from a unit the template does not name. `readValue` reads
    // the result as it reads any other value, so neither reaches the form.
    case 'dateOffset':
      return readValue(dateOffset(node, context));
    case 'sum':
      return readValue(sum(node, context));
    case 'difference':
      return readValue(difference(node, context));
    case 'product':
      return readValue(product(node, context));
    case 'round':
      return readValue(round(node, context));
    case 'firstValue':
      return firstValue(node.values, (value) => resolveValue(value, context));
    case 'when': {
      // The value-tree twin of the `when` label node, and it answers in the
      // same way. A false rule with no else branch gives no value, which
      // leaves the markup bound in place.
      const branch = evaluate(node.when, context) ? node.then : node.else;
      return branch === undefined ? undefined : resolveValue(branch, context);
    }
    case 'option':
      // An option is not a number or a date, so a bound cannot apply it, and
      // the markup bound stays.
      return undefined;
    case 'columnSum':
      return readColumnSum(context, node.path);
    case 'vesselField':
      return readVesselNumber(context, node.path);
    default:
      // Every node type has a branch above. This guards a new type added
      // without a branch: the compiler fails here.
      return assertNever(node);
  }
}

function assertNever(node: never): never {
  throw new Error(`Unknown value node type: ${String((node as { type: string }).type)}`);
}

/**
 * Sums one cell over every row of the tables in a section of the report being
 * edited. A table is an array of rows in the section, as `fuelSpecs` and
 * `totalConsumptions` hold under `entries`.
 *
 * Undefined when the path does not name a tab, a section and a cell, or when
 * the section holds no table. Otherwise the sum of the cells that hold a
 * finite number, which is 0 when none does.
 */
export function readColumnSum(context: RuleContext, path: string): number | undefined {
  const keys = path.split('.');
  if (keys.length !== 3) {
    return undefined;
  }
  const [tabId, sectionId, cellId] = keys;
  const section = readSection(context.formState, tabId, sectionId);
  if (section === null || typeof section !== 'object') {
    return undefined;
  }
  const tables = Object.values(section).filter((rows): rows is unknown[] => Array.isArray(rows));
  if (tables.length === 0) {
    return undefined;
  }
  let sum = 0;
  for (const row of tables.flat()) {
    const cell = (row as Record<string, { value?: unknown } | undefined> | null)?.[cellId];
    const value = cell?.value;
    if (typeof value === 'number' && Number.isFinite(value)) {
      sum += value;
    }
  }
  return sum;
}

/**
 * Reads the number that the vessel spec holds at a path.
 *
 * Undefined for anything other than a finite number. `readValue` is not used,
 * because it reads a string as a date, and a string in the vessel spec is not a
 * date that a bound applies.
 */
function readVesselNumber(context: RuleContext, path: string): number | undefined {
  const value = readVesselField(context, path);
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** How many milliseconds one of each unit holds. */
const MILLISECONDS_IN: Record<ValueDateOffsetNode['unit'], number> = {
  seconds: 1000,
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000,
};

/**
 * Adds an amount of time to a date.
 *
 * A value that is not a date, and an amount that is not a number, are template
 * mistakes. Each gives nothing, which leaves the markup bound in place rather
 * than turning a date into a number the way JavaScript would.
 *
 * It builds a new date, so the date it read stays as it was. The form state
 * holds that date, and a node that changed it would change the report.
 */
function dateOffset(node: ValueDateOffsetNode, context: RuleContext): Date | undefined {
  const value = resolveValue(node.value, context);
  const add = resolveValue(node.add, context);
  if (!(value instanceof Date) || typeof add !== 'number') {
    return undefined;
  }
  return new Date(value.getTime() + add * MILLISECONDS_IN[node.unit]);
}

/**
 * Adds every number in the list. A value that gives no number gives no sum,
 * so the markup bound applies. See ValueSumNode.
 */
function sum(node: ValueSumNode, context: RuleContext): number | undefined {
  const numbers = readNumbers(node.values, context);
  return numbers?.reduce((total, value) => total + value, 0);
}

/**
 * The start value less every number in the list. A value that gives no number
 * gives no difference. See ValueDifferenceNode.
 */
function difference(node: ValueDifferenceNode, context: RuleContext): number | undefined {
  const value = resolveValue(node.value, context);
  const numbers = readNumbers(node.subtract, context);
  if (typeof value !== 'number' || numbers === undefined) {
    return undefined;
  }
  return numbers.reduce((total, subtract) => total - subtract, value);
}

/**
 * Multiplies every number in the list. A value that gives no number gives no
 * product. See ValueProductNode.
 */
function product(node: ValueProductNode, context: RuleContext): number | undefined {
  const numbers = readNumbers(node.values, context);
  return numbers?.reduce((total, value) => total * value, 1);
}

/** How each direction rounds a number to a whole number. V1 uses the same two. */
const ROUND: Record<ValueRoundNode['direction'], (value: number) => number> = {
  up: Math.ceil,
  nearest: Math.round,
};

/**
 * Rounds the number to the given places, with V1's formula. A value that gives
 * no number, and places that are negative or not whole, give no value. See
 * ValueRoundNode.
 */
function round(node: ValueRoundNode, context: RuleContext): number | undefined {
  const value = resolveValue(node.value, context);
  const places = node.places ?? 0;
  if (typeof value !== 'number' || !Number.isInteger(places) || places < 0) {
    return undefined;
  }
  const scale = Math.pow(10, places);
  return ROUND[node.direction](value * scale) / scale;
}

/**
 * The first value that `read` gives for a node of the list, or undefined when
 * it gives none for every node. Both readers use it, each with its own `read`.
 * See ValueFirstValueNode.
 */
export function firstValue<T>(nodes: ValueNode[], read: (node: ValueNode) => T | undefined): T | undefined {
  for (const node of nodes) {
    const value = read(node);
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

/**
 * Reads every node of a list as a number.
 *
 * Undefined for an empty list, and when one node gives anything other than a
 * number. The arithmetic nodes are strict, so a missing value never counts as
 * 0 unless the template says so with `firstValue`.
 */
function readNumbers(nodes: ValueNode[], context: RuleContext): number[] | undefined {
  if (nodes.length === 0) {
    return undefined;
  }
  const numbers: number[] = [];
  for (const node of nodes) {
    const value = resolveValue(node, context);
    if (typeof value !== 'number') {
      return undefined;
    }
    numbers.push(value);
  }
  return numbers;
}

// A date, '2026-01-31', or a date-time with an optional time zone, such as
// '2026-01-31T06:00:00.000Z' or '2026-01-31T06:00:00+01:00'.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Reads a value as the number or the date a bound applies.
 *
 * A finite number is the value. A `Date` is the value, which is the shape a
 * date field of the form holds. A string is an ISO-8601 date-time, because the
 * template reaches the client as JSON and a date arrives as text there.
 *
 * Everything else gives undefined: a value of another type, a number that is
 * not finite, an Invalid Date, and a string that is not an ISO-8601 date. The
 * Date constructor alone also reads text such as '5' as a date, so the string
 * must have the ISO shape first. A bound the resolver cannot read leaves the
 * markup bound in place rather than putting an invalid value on the control.
 */
function readValue(value: unknown): number | Date | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  if (typeof value !== 'string' || !ISO_DATE.test(value)) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}
